import { useCallback } from 'react';

import { laravelOwnsData } from '@/lib/dataOwner';
import { esitoScritturaNickname, type EsitoNickname } from '@/lib/nicknameEsito';
import { useAppDispatch } from '@/store/hooks';
import { setNickname } from '@/store/auth.slice';
import { useUsersMeQuery, useUpdateUserMutation } from '@/services/endpoints/users';
import { usePutLimuloNicknameMutation } from '@/services/limuloApi';

/**
 * Scrivere il nickname — **l'unico posto in cui si decide DOVE va scritto.**
 *
 * ═══ PERCHE' ESISTE UN PUNTO SOLO ═══
 * Il nickname ha due sedi possibili e **sta cambiando sede**:
 *  · **al 2026-09-15 la fonte in produzione e' Laravel** — `PUT /users/:id`, che e' anche
 *    l'unica rotta che `deploy:prod` serve oggi;
 *  · [ADR-0125 `proposed`] il nome pubblico di QLASH **vive in Limulo** — `PUT /v1/me/nickname`,
 *    backend gia' mergiato (Limulo #48/#49) e collaudato su staging.
 * ⇒ La domanda «quale delle due?» non si indovina e non si sparge: si isola. Qui.
 * 📌 *Una dipendenza che sai che cambiera' non si indovina: si isola — e l'isolamento vale
 * anche se l'ADR non passa mai.*
 *
 * ═══ ⚠️ LE DUE ROTTE NON SONO LA STESSA ROTTA CON UN ALTRO URL ═══
 *  · Laravel pretende **l'id nel path e il record intero nel corpo**: mandare il solo
 *    `nickname` fa perdere `country`/`birthday`/`name`/`locale`, perche' l'endpoint
 *    sostituisce invece di fondere. E' la ragione dello spread qui sotto, ed e' la stessa
 *    forma gia' pagata in `components/onboarding/WelcomeStep.tsx`.
 *  · Limulo **non prende un id**: agisce solo sull'attore autenticato, e normalizza
 *    trim/lowercase su `[a-z0-9_]` 3-32.
 * ⇒ I due spazi di id (numerico Laravel contro ObjectId Limulo) non sono intercambiabili:
 *  `lib/dataOwner.ts` sceglie **una volta per build**, mai a runtime.
 *
 * ═══ 🔴 IL VALORE DI RITORNO E' UN ESITO, NON UN BOOLEANO ═══
 * `null` = riuscito. Tutto il resto e' un `EsitoNickname` con un nome
 * (`lib/nicknameEsito.ts`), perche' «gia' preso» e «non lo so» mandano l'utente a fare due
 * cose diverse, e collassarli lo manda a cambiare un nome che andava bene.
 */
export interface SalvaNickname {
  /** `null` = scritto. Altrimenti l'esito che l'utente deve leggere. */
  salva: (nickname: string) => Promise<EsitoNickname | null>;
  /** Una scrittura e' in volo. */
  inCorso: boolean;
  /**
   * ⚠️ `false` = **non abbiamo ancora cio' che serve per scrivere**, non «non si puo'».
   * Su Laravel serve il record corrente (l'endpoint sostituisce); su Limulo no, quindi li'
   * e' vero da subito. Chi disegna il bottone deve distinguerlo da un errore: un bottone
   * spento per un caricamento e uno spento per un divieto si vedono uguali.
   */
  pronto: boolean;
}

export function useSalvaNickname(): SalvaNickname {
  const dispatch = useAppDispatch();
  const suLaravel = laravelOwnsData();

  // ⚠️ Entrambe le mutation si dichiarano SEMPRE: un hook non si chiama sotto condizione.
  // Una mutation non parte al montaggio, quindi dichiararla non tocca la rete — a differenza
  // della query qui sotto, che va spenta per costruzione sulla build che non la possiede.
  const [updateUser, { isLoading: laravelInCorso }] = useUpdateUserMutation();
  const [putNickname, { isLoading: limuloInCorso }] = usePutLimuloNicknameMutation();
  const { data: me, isLoading: meInCorso } = useUsersMeQuery(undefined, { skip: !suLaravel });

  const salva = useCallback(
    async (nickname: string): Promise<EsitoNickname | null> => {
      try {
        if (suLaravel) {
          // ⛔ Senza il record corrente non si scrive: mandare il solo nickname AZZEREREBBE
          // gli altri campi. `pronto` esiste per non far premere il bottone qui.
          if (!me) return 'sconosciuto';
          await updateUser({
            id: me.id,
            nickname,
            country: me.country ?? '',
            birthday: me.birthday ?? '',
            name: me.name ?? '',
            accepted_privacy: me.accepted_privacy ?? true,
            locale: me.locale ?? '',
          }).unwrap();
        } else {
          await putNickname({ nickname }).unwrap();
        }
      } catch (err) {
        return esitoScritturaNickname(err);
      }
      // 🔑 Lo store si allinea QUI e non altrove: `auth.user` non viene da una query, quindi
      // nessuna invalidazione di tag lo tocca. v. `store/auth.slice.ts` → `setNickname`.
      dispatch(setNickname(nickname));
      return null;
    },
    [suLaravel, me, updateUser, putNickname, dispatch],
  );

  return {
    salva,
    inCorso: laravelInCorso || limuloInCorso,
    pronto: suLaravel ? !meInCorso && !!me : true,
  };
}
